import { MapPin } from "lucide-react";
import { useParams } from "react-router-dom";
import LOCATIONS from "@/data/locations.json";
import { CALENDLY_URL } from "@/data/nav";
import { useSeo } from "@/lib/site";
import { PageHero } from "@/components/site/PageHero";
import { Section, Statement, Label, H2, Paras, ServiceBlocks, Process, MetricsCloud, CtaBand, CtaButton, CtaRow } from "@/components/site/Blocks";
import { Reveal } from "@/components/site/Motion";

const ctaBtn = (raw, i, dark = true) => {
  const audit = /audit/i.test(raw);
  const label = audit ? "Book a Meeting" : raw;
  const to = label.startsWith("Explore AI Search") ? "/ai-search-visibility" : undefined;
  return <CtaButton key={label} label={label} to={to} href={audit ? CALENDLY_URL : undefined} variant={i ? (dark ? "ghost" : "outline") : dark ? "primary" : "dark"} testid={`loc-cta-${label.toLowerCase().replace(/[^a-z]+/g, "-")}-${i}`} />;
};

const heroLines = (title, city) => {
  const base = title.endsWith(` ${city}`) ? title.slice(0, -city.length - 1) : null;
  const m = base && base.match(/^(.*?)(Visibility Agency)$/);
  if (!m) return [title];
  return [m[1].trim(), <span className="font-serif-i text-gradient">{m[2]}</span>, city];
};

const CityVisual = ({ city }) => (
  <div className="absolute inset-0 overflow-hidden bg-[#0d0a24]">
    <div className="grid-lines absolute inset-0" />
    {[30, 55, 80, 105].map((s) => (
      <div key={s} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-violet-400/20" style={{ width: `${s}%`, aspectRatio: "1" }} />
    ))}
    <div className="radar-ping absolute left-1/2 top-1/2 h-[90%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-violet-400/50" style={{ aspectRatio: "1" }} />
    <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
      <MapPin className="h-10 w-10 text-violet-200" fill="#8B5CF6" />
      <span className="mt-3 font-display text-3xl font-medium text-white sm:text-4xl">{city}</span>
    </div>
    <p className="absolute bottom-6 left-6 right-6 font-mono text-[11px] uppercase tracking-[0.22em] text-violet-200">Serving businesses in {city}</p>
  </div>
);

const AiSection = ({ s, n }) => (
  <Section dark tone="plum" testid="loc-ai-section">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Label n={n} dark>AI search</Label></Reveal>
        <Reveal delay={0.05}><H2 dark className="mt-6">{s.title}</H2></Reveal>
        <Reveal delay={0.1}><Paras dark className="mt-6" items={s.paras} /></Reveal>
        {s.ctas.length > 0 && <CtaRow className="mt-10">{s.ctas.map((c, i) => ctaBtn(c, i))}</CtaRow>}
      </div>
      <div className="grid content-start gap-4 lg:col-span-6 lg:col-start-7">
        {s.subs.map((x, i) => (
          <Reveal key={x.title} delay={i * 0.08} className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 sm:p-8">
            <h3 className="font-display text-2xl font-medium text-white">{x.title}</h3>
            <Paras dark className="mt-3" items={x.paras} />
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

const renderSection = (s, i, city, isLast, tone) => {
  const n = String(i + 1).padStart(2, "0");
  if (isLast && s.ctas.length) {
    return <CtaBand key={s.title} testid="loc-final-cta" title={[s.title]} paras={s.paras} ctas={<>{s.ctas.map((c, k) => ctaBtn(c, k))}</>} />;
  }
  if (s.bullets.length) return <MetricsCloud key={s.title} n={n} title={s.title} intro={s.paras} items={s.bullets} />;
  if (s.subs.length && /^\d+\.\s/.test(s.subs[0].title)) {
    return <Process key={s.title} n={n} title={s.title} steps={s.subs.map((x) => ({ title: x.title.replace(/^\d+\.\s*/, ""), text: x.paras.join(" ") }))} />;
  }
  if (s.subs.length && s.title.startsWith("AI Search")) return <AiSection key={s.title} s={s} n={n} />;
  if (s.subs.length) {
    return (
      <Section key={s.title} tone="subtle" testid="loc-services-section">
        <Reveal><Label n={n}>Services</Label></Reveal>
        <Reveal delay={0.05}><H2 className="mt-6">{s.title}</H2></Reveal>
        {s.paras.length > 0 && <Paras className="mt-6 max-w-2xl" items={s.paras} />}
        <ServiceBlocks items={s.subs.map((x) => ({ title: x.title, paras: x.paras }))} testid="loc-service-blocks" />
      </Section>
    );
  }
  const label = s.title.startsWith("Why") ? "Why us" : s.title.startsWith("SEO for") ? "Sectors" : city;
  return <Statement key={s.title} n={n} label={label} title={s.title} paras={s.paras} tone={tone} testid={`loc-statement-${i}`} />;
};

export default function LocationPage({ slug: fixed }) {
  const params = useParams();
  const page = LOCATIONS.find((l) => l.slug === (fixed || params.slug)) || LOCATIONS[0];
  useSeo(page.meta["SEO Title"], page.meta["Meta Description"]);
  const { hero, sections, city } = page;
  let light = 0;
  return (
    <div data-testid={`location-page-${page.slug}`}>
      <PageHero
        crumb={["Locations", city]}
        title={heroLines(hero.title, city)}
        sub={hero.sub}
        paras={hero.paras}
        ctas={<>{hero.ctas.map((c, i) => ctaBtn(c, i))}</>}
        visual={<CityVisual city={city} />}
      />
      {sections.map((s, i) => {
        const plain = !s.subs.length && !s.bullets.length && !(i === sections.length - 1 && s.ctas.length);
        const tone = plain ? (light++ % 2 ? "subtle" : undefined) : undefined;
        return renderSection(s, i, city, i === sections.length - 1, tone);
      })}
    </div>
  );
}

export const LOCATION_LINKS = LOCATIONS.map((l) => ({ label: `SEO Agency ${l.city}`, to: `/${l.slug}`, slug: l.slug }));
